import React, { useState, useEffect, useCallback, Suspense, lazy, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from './supabaseClient';
import {
  clearPinFailures,
  formatPinFailureMessage,
  formatPinLockMessage,
  getPinLockState,
  recordPinFailure,
} from './services/pinSecurity';
import { logSecurityEvent } from './services/securityAudit';
import { verifyStaffPin } from './services/staffPinAuth';
import { getCurrentRestaurantBrand } from './services/restaurantBrand';
import { getActiveRestaurantSession, setActiveRestaurantSession } from './services/restaurantSession';
import { revokeRiderSession, validateRiderSession } from './services/riderPortal';
import { isKdsEligibleStaff } from './utils/kdsAccess';

// Components
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import UserFeedback from './modules/UserFeedback';

// Lazy load major routes
const RiderLogin = lazy(() => import('./modules/RiderLogin'));
const RiderDashboard = lazy(() => import('./modules/RiderDashboard'));
const KDS = lazy(() => import('./modules/KDS'));
const StaffKiosk = lazy(() => import('./modules/StaffKiosk'));
const PublicStore = lazy(() => import('./modules/PublicStore'));
const StaffPortal = lazy(() => import('./modules/StaffPortal'));
const OnboardingChecklist = lazy(() => import('./modules/OnboardingChecklist'));

const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center bg-[#0D0D0D]">
    <Loader2 className="animate-spin text-[#F25C05]" size={48} />
  </div>
);

const getAuthLinkType = () => {
  if (typeof window === 'undefined') return '';
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return search.get('type') || hash.get('type') || '';
};

const isPasswordSetupLink = () => ['invite', 'recovery'].includes(getAuthLinkType());

const PasswordSetupScreen = ({ session, onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState({ loading: false, message: '', error: '' });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextPassword = password.trim();

    if (nextPassword.length < 6) {
      setState({ loading: false, message: '', error: 'Password kam az kam 6 characters hona chahiye.' });
      return;
    }

    if (nextPassword !== confirmPassword.trim()) {
      setState({ loading: false, message: '', error: 'Password confirmation match nahi kar rahi.' });
      return;
    }

    setState({ loading: true, message: '', error: '' });
    const { error } = await supabase.auth.updateUser({ password: nextPassword });
    if (error) {
      setState({ loading: false, message: '', error: error.message || 'Password set nahi ho saka.' });
      return;
    }

    setState({ loading: false, message: 'Password set ho gaya. Account open ho raha hai...', error: '' });
    setPassword('');
    setConfirmPassword('');
    window.history.replaceState({}, document.title, '/');
    setTimeout(onComplete, 500);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] p-6 font-space text-white">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg overflow-hidden rounded-[28px] border border-[#F25C05]/40 bg-[#111] shadow-2xl shadow-[#F25C05]/10"
      >
        <div className="border-b border-[#272727] bg-[#191919] p-8">
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#F25C05]">Secure Invite</p>
          <h1 className="mt-3 text-3xl font-black uppercase italic tracking-tight">Set Your Password</h1>
          <p className="mt-3 text-sm font-bold leading-relaxed text-gray-400">
            {session?.user?.email || 'Invited user'} ke liye password set karein. Iske baad account assigned branch/role ke sath open ho jayega.
          </p>
        </div>

        <div className="space-y-4 p-8">
          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-gray-500">
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoFocus
              className="w-full rounded-2xl border border-[#333] bg-[#050505] px-5 py-4 text-sm font-black text-white outline-none transition-all focus:border-[#F25C05]"
              placeholder="Enter password"
            />
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-gray-500">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="w-full rounded-2xl border border-[#333] bg-[#050505] px-5 py-4 text-sm font-black text-white outline-none transition-all focus:border-[#F25C05]"
              placeholder="Re-enter password"
            />
          </div>

          {(state.error || state.message) && (
            <div className={`rounded-2xl border p-4 text-sm font-bold ${
              state.error
                ? 'border-red-500/30 bg-red-500/10 text-red-300'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
            }`}>
              {state.error || state.message}
            </div>
          )}

          <button
            type="submit"
            disabled={state.loading}
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#F25C05] py-4 text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#F25C05]/20 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {state.loading ? <Loader2 className="animate-spin" size={17} /> : null}
            Save Password
          </button>
        </div>
      </form>
    </div>
  );
};

const shortRestaurantId = (restaurantId) => String(restaurantId || '').slice(0, 8).toUpperCase();

const cleanRestaurantName = (value) => {
  const name = String(value || '').trim();
  if (!name) return '';
  if (/^branch\s+\d+$/i.test(name)) return '';
  if (/^restaurant\s+[0-9a-f-]+$/i.test(name)) return '';
  return name;
};

const getRestaurantDisplayName = (restaurant) => cleanRestaurantName(
  restaurant?.name ||
  restaurant?.restaurant_name ||
  restaurant?.business_name ||
  restaurant?.branch_name ||
  restaurant?.display_name ||
  ''
);

const getRestaurantFallbackName = (restaurantId) => `Restaurant ${shortRestaurantId(restaurantId) || 'Selected'}`;

const getStoredRestaurantName = (restaurantId) => {
  if (!restaurantId || localStorage.getItem('currentRestaurantId') !== restaurantId) return '';
  return cleanRestaurantName(localStorage.getItem('currentRestaurantName'));
};

const normalizeMembershipRole = (role) => String(role || '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ');

const canManageTenantOnboarding = (role) => (
  ['owner', 'admin', 'super admin', 'superadmin', 'manager'].includes(normalizeMembershipRole(role))
);

const KDSPasswordGate = ({ children }) => {
  const pinScope = 'kds';
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [brandTitle, setBrandTitle] = useState(() => localStorage.getItem('currentRestaurantName') || '');
  const [brandLogo, setBrandLogo] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem('kds_unlocked') === 'true');

  useEffect(() => {
    let cancelled = false;
    getCurrentRestaurantBrand()
      .then((brand) => {
        if (cancelled) return;
        setBrandTitle(brand.displayName || brand.name || '');
        setBrandLogo(brand.logoUrl || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleLock = () => {
    sessionStorage.removeItem('kds_unlocked');
    sessionStorage.removeItem('kds_staff_id');
    sessionStorage.removeItem('kds_staff_name');
    setIsUnlocked(false);
    setPin('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentRestaurantId = localStorage.getItem('currentRestaurantId');
    const cleanPin = pin.trim();
    const lockState = getPinLockState(pinScope);

    if (!currentRestaurantId) {
      setError('Restaurant context missing. Open admin first, then open KDS.');
      return;
    }

    if (lockState.locked) {
      void logSecurityEvent({
        eventType: 'kds_pin_locked',
        actorType: 'kds_staff',
        outcome: 'blocked',
        reason: 'Local PIN attempt lock is active',
      });
      setError(formatPinLockMessage(lockState.remainingMs));
      setPin('');
      return;
    }

    if (cleanPin.length < 4) {
      setError('Enter your staff PIN.');
      return;
    }

    setLoading(true);
    try {
      const staffMember = await verifyStaffPin(cleanPin, { kitchenOnly: true, scope: pinScope });

      if (!staffMember) {
        const failure = recordPinFailure(pinScope);
        void logSecurityEvent({
          eventType: 'kds_pin_login',
          actorType: 'kds_staff',
          outcome: failure.locked ? 'locked' : 'failed',
          reason: 'Invalid KDS staff PIN',
          deviceContext: { remainingAttempts: failure.remainingAttempts },
        });
        setError(formatPinFailureMessage(failure));
        setPin('');
        return;
      }

      if (!isKdsEligibleStaff(staffMember)) {
        void logSecurityEvent({
          eventType: 'kds_pin_login',
          actorType: 'staff',
          actorId: staffMember.id,
          actorName: staffMember.name,
          outcome: 'denied',
          reason: 'Valid staff PIN without KDS permission',
          deviceContext: {
            role: staffMember.role || '',
            department: staffMember.department || '',
          },
        });
        setError('This staff PIN is valid but not allowed to open KDS.');
        setPin('');
        return;
      }

      clearPinFailures(pinScope);
      void logSecurityEvent({
        eventType: 'kds_pin_login',
        actorType: 'staff',
        actorId: staffMember.id,
        actorName: staffMember.name,
        outcome: 'success',
        reason: 'KDS unlocked',
        deviceContext: {
          role: staffMember.role || '',
          department: staffMember.department || '',
        },
      });
      const verifiedStaffId = staffMember.id || staffMember.staff_id || staffMember.staffId || '';
      sessionStorage.setItem('kds_unlocked', 'true');
      sessionStorage.setItem('kds_staff_id', verifiedStaffId ? String(verifiedStaffId) : '');
      sessionStorage.setItem('kds_staff_name', staffMember.name || 'Kitchen Staff');
      sessionStorage.setItem('kds_request_fullscreen', 'true');
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (_) {
        // Fullscreen may require a direct user gesture in some browsers; KDS mount retries.
      }
      setIsUnlocked(true);
      setError('');
      setPin('');
    } catch (err) {
      console.error('KDS access failed:', err);
      void logSecurityEvent({
        eventType: 'kds_pin_login',
        actorType: 'kds_staff',
        outcome: 'error',
        reason: err.message || 'KDS access failed',
      });
      setError(err.message || 'KDS access failed.');
    } finally {
      setLoading(false);
    }
  };

  if (isUnlocked) return React.cloneElement(children, { onLockKDS: handleLock });

  return (
    <div className="min-h-screen bg-[#050505] text-white font-space flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-[32px] border border-[#F25C05]/40 bg-[#111] p-8 shadow-2xl shadow-[#F25C05]/10"
      >
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F25C05] shadow-lg shadow-[#F25C05]/30 overflow-hidden">
            {brandLogo ? (
              <img src={brandLogo} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="material-symbols-outlined text-3xl text-white">restaurant</span>
            )}
          </div>
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#F25C05]">Kitchen Access</p>
          <h1 className="mt-2 text-3xl font-black uppercase italic tracking-tight">
            {brandTitle ? `${brandTitle} KDS` : 'KDS Login'}
          </h1>
          <p className="mt-2 text-xs font-bold uppercase tracking-widest text-gray-500">
            Enter kitchen staff PIN to open order display
          </p>
        </div>

        <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-gray-500">
          Staff PIN
        </label>
        <input
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
            setError('');
          }}
          autoFocus
          className="w-full rounded-2xl border border-[#333] bg-[#0d0d0d] px-5 py-4 text-center text-lg font-black tracking-widest text-white outline-none transition-all focus:border-[#F25C05]"
          placeholder="••••••••"
        />

        {error && (
          <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center text-xs font-black uppercase tracking-widest text-red-400">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-2xl bg-[#F25C05] py-4 text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#F25C05]/20 transition-all hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Checking Access...' : 'Unlock Kitchen Display'}
        </button>

        <p className="mt-5 text-center text-[10px] font-bold uppercase leading-relaxed tracking-widest text-gray-600">
          Tenant-scoped access. Only kitchen staff, manager, or admin can unlock.
        </p>
      </form>
    </div>
  );
};

const App = () => {
  // --- 🔒 AUTH & UI STATE ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentRestaurantId, setCurrentRestaurantId] = useState(localStorage.getItem('currentRestaurantId') || null);
  const [restaurantMemberships, setRestaurantMemberships] = useState([]);
  const [authSession, setAuthSession] = useState(null);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState(() => isPasswordSetupLink());
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('pizza_theme');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const activeMembership = useMemo(
    () => restaurantMemberships.find(row => row.restaurant_id === currentRestaurantId) || null,
    [restaurantMemberships, currentRestaurantId]
  );
  const shouldGateOnboarding = isAuthenticated && canManageTenantOnboarding(activeMembership?.role);

  // Rider Auth State
  const [riderAuth, setRiderAuth] = useState(() => JSON.parse(localStorage.getItem('rider_auth')) || null);

  const loadUserRestaurants = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('user_restaurants')
      .select('restaurant_id, role, is_active')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('restaurant_id');

    if (error) throw error;

    const restaurantIds = (data || []).map(row => row.restaurant_id).filter(Boolean);
    const { data: restaurantRows, error: restaurantError } = restaurantIds.length
      ? await supabase
        .from('restaurants')
        .select('id,name,subdomain,custom_domain')
        .in('id', restaurantIds)
      : { data: [], error: null };

    if (restaurantError) throw restaurantError;

    const namesById = new Map((restaurantRows || []).map(row => [row.id, getRestaurantDisplayName(row)]));
    const memberships = (data || []).map((row) => ({
      restaurant_id: row.restaurant_id,
      role: row.role || 'member',
      name: namesById.get(row.restaurant_id) || getStoredRestaurantName(row.restaurant_id) || getRestaurantFallbackName(row.restaurant_id),
    }));

    setRestaurantMemberships(memberships);
    localStorage.setItem('authorizedRestaurantIds', JSON.stringify(memberships.map(m => m.restaurant_id).filter(Boolean)));
    return memberships;
  }, []);

  const cacheSelectedRestaurant = useCallback((selected) => {
    if (!selected) return;
    setCurrentRestaurantId(selected.restaurant_id);
    localStorage.setItem('currentRestaurantId', selected.restaurant_id);
    localStorage.setItem('currentRestaurantName', selected.name);
  }, []);

  const resolveActiveRestaurant = useCallback(async (memberships) => {
    if (!memberships?.length) return null;

    let serverSession = null;
    try {
      const { data, error } = await getActiveRestaurantSession();
      if (!error) serverSession = data;
      else if (!['42883', 'PGRST202'].includes(error.code)) {
        console.warn('Active restaurant session unavailable:', error.message);
      }
    } catch (err) {
      console.warn('Active restaurant session lookup failed:', err.message);
    }

    const serverSelected = serverSession?.restaurant_id
      ? memberships.find(m => m.restaurant_id === serverSession.restaurant_id)
      : null;
    if (serverSelected) return serverSelected;

    const storedRestaurantId = localStorage.getItem('currentRestaurantId');
    const storedSelected = memberships.find(m => m.restaurant_id === storedRestaurantId);
    const selected = storedSelected || memberships[0];

    if (selected) {
      void setActiveRestaurantSession(selected.restaurant_id);
    }

    return selected;
  }, []);

  // --- 💾 SESSION & THEME PERSISTENCE ---
  useEffect(() => {
    // Initial Session Check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setAuthSession(session || null);
      if (isPasswordSetupLink()) {
        setPasswordSetupRequired(true);
      }
      if (session) {
        setIsAuthenticated(true);
        try {
          const memberships = await loadUserRestaurants(session.user.id);
          const selected = await resolveActiveRestaurant(memberships);
          cacheSelectedRestaurant(selected);
        } catch (err) {
          console.error('Restaurant membership load failed:', err);
        }
      }
    });

    // Listen for Auth Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthSession(session || null);
      if (event === 'PASSWORD_RECOVERY' || isPasswordSetupLink()) {
        setPasswordSetupRequired(true);
      }
      if (session) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setCurrentRestaurantId(null);
        setRestaurantMemberships([]);
        localStorage.removeItem('currentRestaurantId');
        localStorage.removeItem('currentRestaurantName');
        localStorage.removeItem('authorizedRestaurantIds');
        setPasswordSetupRequired(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserRestaurants, resolveActiveRestaurant, cacheSelectedRestaurant]);

  useEffect(() => {
    localStorage.setItem('pizza_theme', JSON.stringify(isDark));
    if (isDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDark]);

  useEffect(() => {
    if (!riderAuth) {
      localStorage.removeItem('rider_auth');
      return;
    }

    localStorage.setItem('rider_auth', JSON.stringify(riderAuth));
    const isRiderRoute = window.location.pathname.startsWith('/rider');
    if (!isAuthenticated && isRiderRoute && riderAuth.restaurant_id) {
      localStorage.setItem('currentRestaurantId', riderAuth.restaurant_id);
      setCurrentRestaurantId(riderAuth.restaurant_id);
    }
  }, [riderAuth, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    const validateStoredRider = async () => {
      if (!riderAuth?.session_token || !riderAuth?.restaurant_id) return;
      if (riderAuth.session_expires_at && new Date(riderAuth.session_expires_at).getTime() <= Date.now()) {
        if (!cancelled) setRiderAuth(null);
        return;
      }

      const { data, error } = await validateRiderSession({
        token: riderAuth.session_token,
        restaurantId: riderAuth.restaurant_id,
      });

      if (cancelled) return;
      if (error || !data) {
        setRiderAuth(null);
        return;
      }

      setRiderAuth(prev => {
        if (!prev || prev.session_token !== riderAuth.session_token) return prev;
        return {
          ...prev,
          id: data.rider_id,
          name: data.rider_name,
          staff_id: data.staff_id,
          staff_name: data.staff_name,
          staff_role: data.staff_role,
          staff_department: data.staff_department,
          restaurant_id: data.restaurant_id,
          session_expires_at: data.expires_at,
        };
      });
    };

    validateStoredRider();
    return () => {
      cancelled = true;
    };
  }, [riderAuth?.session_token, riderAuth?.restaurant_id, riderAuth?.session_expires_at]);

  const handleAdminLogin = useCallback(async (e) => {
    e.preventDefault();
    try {
      // 1. Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: usernameInput, // Use email for login
        password: passwordInput
      });

      if (authError) throw authError;

      // 2. Fetch associated restaurants
      localStorage.removeItem('rider_auth');
      setRiderAuth(null);
      const userRestaurants = await loadUserRestaurants(authData.user.id);

      if (userRestaurants && userRestaurants.length > 0) {
        const selected = await resolveActiveRestaurant(userRestaurants);
        cacheSelectedRestaurant(selected);
        setIsAuthenticated(true);
      } else {
        alert("Success! But no restaurants are linked to your account. Please contact admin.");
        await supabase.auth.signOut();
      }
    } catch (err) {
      console.error("Auth Exception:", err);
      alert("Login Error: " + err.message);
    }
  }, [usernameInput, passwordInput, loadUserRestaurants, resolveActiveRestaurant, cacheSelectedRestaurant]);

  const handleRestaurantSwitch = useCallback(async (restaurantId) => {
    const selected = restaurantMemberships.find(r => r.restaurant_id === restaurantId);
    if (!selected || selected.restaurant_id === currentRestaurantId) return;

    const { error } = await setActiveRestaurantSession(selected.restaurant_id);
    if (error) {
      alert(`Restaurant switch failed: ${error.message}`);
      return;
    }

    localStorage.setItem('currentRestaurantId', selected.restaurant_id);
    localStorage.setItem('currentRestaurantName', selected.name);
    localStorage.removeItem('rider_auth');
    sessionStorage.removeItem('kds_unlocked');
    sessionStorage.removeItem('kds_staff_id');
    sessionStorage.removeItem('kds_staff_name');
    setRiderAuth(null);
    setCurrentRestaurantId(selected.restaurant_id);
    window.location.reload();
  }, [restaurantMemberships, currentRestaurantId]);

  const handleLogout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Logout Error:", error.message);
  }, []);

  const handleRiderLogout = useCallback(async () => {
    const token = riderAuth?.session_token;
    const restaurantId = riderAuth?.restaurant_id;
    setRiderAuth(null);
    if (token && restaurantId) {
      const { error } = await revokeRiderSession({ token, restaurantId, reason: 'rider_logout' });
      if (error) console.error('Rider session revoke failed:', error.message);
    }
  }, [riderAuth]);

  const refreshOnboardingStatus = useCallback(async (restaurantIdOverride = null) => {
    const restaurantId = restaurantIdOverride || localStorage.getItem('currentRestaurantId');
    const selectedMembership = restaurantMemberships.find(row => row.restaurant_id === restaurantId);
    if (!restaurantId || !isAuthenticated || !canManageTenantOnboarding(selectedMembership?.role)) {
      setOnboardingRequired(false);
      setOnboardingLoading(false);
      return;
    }
    setOnboardingLoading(true);
    try {
      const [settings, staffRows, inventoryRows, categoryRows] = await Promise.all([
        supabase
          .from('restaurant_settings')
          .select('display_name,phone,address')
          .eq('restaurant_id', restaurantId)
          .maybeSingle(),
        supabase
          .from('team_members')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .limit(1),
        supabase
          .from('inventory_items')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .limit(1),
        supabase
          .from('expense_categories')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .limit(1),
      ]);

      const profileDone = Boolean(
        settings.data?.display_name &&
        (settings.data?.phone || settings.data?.address)
      );
      const staffDone = (staffRows.data || []).length > 0;
      const inventoryDone = (inventoryRows.data || []).length > 0;
      const categoryDone = (categoryRows.data || []).length > 0;
      const completed = profileDone && staffDone && inventoryDone && categoryDone;

      setOnboardingRequired(!completed);

      await supabase
        .from('tenant_onboarding_status')
        .upsert({
          restaurant_id: restaurantId,
          profile_done: profileDone,
          staff_done: staffDone,
          inventory_done: inventoryDone,
          category_done: categoryDone,
          onboarding_completed: completed,
          completed_at: completed ? new Date().toISOString() : null,
        }, { onConflict: 'restaurant_id' });
    } catch (error) {
      console.warn('Onboarding status check failed:', error?.message || error);
      setOnboardingRequired(false);
    } finally {
      setOnboardingLoading(false);
    }
  }, [isAuthenticated, restaurantMemberships]);

  useEffect(() => {
    refreshOnboardingStatus();
  }, [currentRestaurantId, refreshOnboardingStatus]);

  const completePasswordSetup = useCallback(async () => {
    setPasswordSetupRequired(false);
    const { data: { session } } = await supabase.auth.getSession();
    setAuthSession(session || null);
    if (!session) return;

    setIsAuthenticated(true);
    try {
      const memberships = await loadUserRestaurants(session.user.id);
      const selected = await resolveActiveRestaurant(memberships);
      cacheSelectedRestaurant(selected);
    } catch (err) {
      console.error('Restaurant membership load failed after password setup:', err);
    }
  }, [loadUserRestaurants, resolveActiveRestaurant, cacheSelectedRestaurant]);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Admin Routes */}
          <Route path="/" element={
            !isAuthenticated ? (
              <AdminLogin
                usernameInput={usernameInput}
                setUsernameInput={setUsernameInput}
                passwordInput={passwordInput}
                setPasswordInput={setPasswordInput}
                pinInput={pinInput}
                setPinInput={setPinInput}
                handleAdminLogin={handleAdminLogin}
              />
            ) : passwordSetupRequired ? (
              <PasswordSetupScreen session={authSession} onComplete={completePasswordSetup} />
            ) : (
              onboardingLoading ? (
                <LoadingScreen />
              ) : shouldGateOnboarding && onboardingRequired ? (
                <OnboardingChecklist onComplete={() => refreshOnboardingStatus()} />
              ) : (
                <AdminPanel
                  isDark={isDark}
                  setIsDark={setIsDark}
                  handleLogout={handleLogout}
                  currentRestaurantId={currentRestaurantId}
                  restaurantMemberships={restaurantMemberships}
                  onRestaurantSwitch={handleRestaurantSwitch}
                />
              )
            )
          } />

          {/* Rider Routes */}
          <Route path="/rider-login" element={
            riderAuth ? <Navigate to="/rider-dashboard" /> : <RiderLogin isDark={isDark} onLogin={setRiderAuth} />
          } />
          <Route path="/rider-dashboard" element={
            riderAuth ? <RiderDashboard rider={riderAuth} onLogout={handleRiderLogout} /> : <Navigate to="/rider-login" />
          } />

          {/* KDS Route */}
          <Route path="/kds" element={<KDSPasswordGate><KDS /></KDSPasswordGate>} />

          {/* Staff Kiosk Route */}
          <Route path="/kiosk" element={<StaffKiosk />} />
          <Route path="/staff-kiosk" element={<StaffKiosk />} />

          {/* Staff Portal Route */}
          <Route path="/my-portal" element={<StaffPortal />} />

          {/* Feedback Route */}
          <Route path="/userfeedback" element={<UserFeedback />} />

          {/* Public Store Routes */}
          <Route path="/order" element={<PublicStore />} />
          <Route path="/order/:restaurantId" element={<PublicStore />} />
          <Route path="/orders" element={<PublicStore />} />
          <Route path="/orders/:restaurantId" element={<PublicStore />} />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
